import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, IconButton,
  Stack,
  TextField, Typography
} from "@mui/material";
import {TopicRepository} from "../../../../repositories/TopicRepository";
import {toast} from "react-toastify";
import React, {useEffect, useState} from "react";
import {OrganizationRepository} from "../../../../repositories/OrganizationRepository";
import {IconPlus, IconX} from "@tabler/icons-react";
import {v4 as uuid} from "uuid";
import {EntityRelationshipRepository} from "../../../../repositories/EntityRelationshipRepository";

export const CreateTopicModal = ({open, handleClose, organization, selectedTopic, onDelete, onSuccess}) => {
  const [entities, setEntities] = useState([]);
  const [relations, setRelations] = useState([]);
  const [entityRelationships, setEntityRelationships] = useState([]);

  useEffect(() => {
    if (organization) {
      OrganizationRepository.getEntities(organization?.id || organization)
        .then(response => setEntities([...new Set(response?.data?.map(item => item?.name) || [])]))
        .catch(error => console.log(error));
      OrganizationRepository.getRelations(organization?.id || organization)
        .then(response => setRelations([...new Set(response?.data?.map(item => item?.name) || [])]))
        .catch(error => console.log(error));
    }
  }, [organization]);


  useEffect(() => {
    setEntityRelationships([]);
  }, [open])

  useEffect(() => {
    if (selectedTopic?.id) {
      const sources = selectedTopic?.relationships?.map(item => item?.source?.name || item?.source) || [];
      const targets = selectedTopic?.relationships?.map(item => item?.target?.name || item?.target) || [];
      const relations = selectedTopic?.relationships?.map(item => item?.relation?.name || item?.relation) || [];
      const entities = [...new Set(sources, targets)];

      setEntities(entities);
      setRelations(relations);
      setEntityRelationships(selectedTopic?.relationships?.map(item => ({id: item?.id, source: item?.source?.name || item?.source, relation: item?.relation?.name || item?.relation, target: item?.target?.name || item?.target})))
    }
  }, [selectedTopic]);

  const handleDelete = () => {
    if (selectedTopic) {
      TopicRepository.delete(selectedTopic.id)
        .then(() => {
          onDelete();
          handleClose();
          toast.success("Topic Deleted");
        })
        .catch(err => toast.error(err.message));
    }
  }

  const addRelationship = () => {
    setEntityRelationships(prev => [{uid: uuid(), source: "", target: "", relation: ""}, ...prev])
  }

  const onDeleteRelation = (item) => {
    if (item.id) {
      EntityRelationshipRepository.delete(item?.id)
        .then(response => {
          toast.success("Relationship is deleted")
          setEntityRelationships(prev => prev?.filter(i => i?.uid !== item?.uid));
        })
        .catch(error => toast.error(error?.message || "Failed to delete relationship"))
    } else if (item?.uid) {
      setEntityRelationships(prev => prev?.filter(i => i?.uid !== item?.uid))
    }
  }

  return (
    <Dialog
      open={open}
      // onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit: (event) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          const formJson = Object.fromEntries((formData).entries());
          formJson.organizationId = organization?.id;
          formJson.relationships = entityRelationships || [];
          if (!formJson.name) {
            toast.error("Name is required field")
            return;
          }

          if (selectedTopic && selectedTopic?.id) {
            TopicRepository.edit(selectedTopic?.id, formJson)
              .then(() => {
                onSuccess();
                toast.success("Topic Updated");
              })
              .catch(err => {
                toast.error("Failed to create topic");
              })
              .finally(() => {
                handleClose();
              });
          } else {
            TopicRepository.create(formJson)
              .then(() => {
                onSuccess();
                toast.success("Topic Created");
              })
              .catch(err => {
                toast.error("Failed to edit topic");
              })
              .finally(() => {
                handleClose();
              });
          }
        },
        sx: {
          maxHeight: "70dvh",
          overflowY: "auto"
        }
      }}
    >
      <DialogTitle>{selectedTopic ? "Edit" : "Create"} Topic</DialogTitle>
      <DialogContent>
        <Stack direction={"column"} gap={1}>
          <Typography fontSize={12}>
            In natural language processing (NLP), a "topic" generally refers to the subject matter or theme of a piece of text or conversation. It's the main idea or subject being discussed.
          </Typography>
          <TextField
            autoFocus
            required
            defaultValue={selectedTopic?.name || ""}
            margin="dense"
            id="name"
            name="name"
            label="Name"
            fullWidth
            variant="outlined"
          />

          <Stack direction={"row"} gap={2} justifyContent={"space-between"}>
            <Typography>Relations</Typography>
            <IconButton onClick={addRelationship} size={"small"}><IconPlus size={18} /></IconButton>
          </Stack>
          {entityRelationships?.map(relationship => {
            const {source, target, relation} = relationship;
            const id = relationship?.id || relationship?.uid
            return (
              <Stack key={id} direction={"row"} gap={1}>
                <Autocomplete
                  sx={{flex: 1}}
                  size={"small"}
                  renderInput={(params) => <TextField {...params} size={"small"} variant={"standard"} label="Source"/>}
                  options={entities} value={source}
                  onInputChange={(e, value) => {
                    setEntityRelationships(prev => prev?.map(i => i?.id === id || i?.uid === id ? {...i, source: value} : {...i}))
                  }}
                  onChange={(e, value) => {
                    setEntityRelationships(prev => prev?.map(i => i?.id === id || i?.uid === id ? {...i, source: value} : {...i}))
                  }}
                  onBlur={() => source && setEntities(prev => [...new Set([...prev, source])])}
                  id="source"
                />
                <Autocomplete
                  sx={{flex: 1}}
                  size={"small"}
                  renderInput={(params) => <TextField {...params} size={"small"} variant={"standard"} label="Relation"/>}
                  options={relations} value={relation}
                  onInputChange={(e, value) => {
                    setEntityRelationships(prev => prev?.map(i => i?.id === id || i?.uid === id ? {...i, relation: value} : {...i}))
                  }}
                  onChange={(e, value) => {
                    setEntityRelationships(prev => prev?.map(i => i?.id === id || i?.uid === id ? {...i, relation: value} : {...i}))
                  }}
                  onBlur={() => relation && setRelations(prev => [...new Set([...prev, relation])])}

                  id="relation"
                />
                <Autocomplete
                  sx={{flex: 1}}
                  size={"small"}
                  renderInput={(params) => <TextField {...params} size={"small"} variant={"standard"} label="Target"/>}
                  options={entities} value={target}
                  onInputChange={(e, value) => {
                    setEntityRelationships(prev => prev?.map(i => i?.id === id || i?.uid === id ? {...i, target: value} : {...i}))
                  }}
                  onChange={(e, value) => {
                    setEntityRelationships(prev => prev?.map(i => i?.id === id || i?.uid === id ? {...i, target: value} : {...i}))
                  }}
                  onBlur={() => target && setEntities(prev => [...new Set([...prev, target])])}
                  id="target"
                />
                <Box>
                  <IconButton size={"small"} onClick={() => onDeleteRelation(relationship)}>
                    <IconX size={18} />
                  </IconButton>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        {selectedTopic && <Button color={"error"} onClick={handleDelete} sx={{mr: "auto"}}>Delete</Button>}
        <Button onClick={handleClose}>Cancel</Button>
        <Button type="submit">{selectedTopic ? "Edit" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  )
}